import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';

import useComponentMounted from '../../../hooks/useComponentMounted';
import Coach from '../../../Model/Coach';
import CoachVoice from '../../../Model/CoachVoice';
import FirebaseContext from '../../../context/FirebaseContext';

import GenericDataGrid from '../../components/GenericDataGrid';
import QuickSearchToolbar from '../../components/QuickSearchToolbar';
import useQuickSearch from '../../../hooks/useQuickSearch';

import {
  HeaderRow, TitleContainer, SubTitle, Title,
} from '../../../components/v2/Header';
import { PrimaryButton } from '../../../components/Button/ActionButtons';
import { Container } from './styles';
import texts from './texts.json';

const getColumns = ({ renderActionCell }) => [
  {
    field: 'name',
    type: 'string',
    headerName: texts.columns.name,
    flex: 2,
    quickSearch: true,
  },
  {
    field: 'status',
    headerName: texts.columns.status,
    flex: 1,
    type: 'string',
  },
  {
    field: 'action',
    headerName: texts.columns.action,
    flex: 1,
    renderCell: renderActionCell,
    sortable: false,
  },
];

const CoachVoiceDashboard = () => {
  const {
    firebase: { remote },
  } = useContext(FirebaseContext);
  const isComponentMountedRef = useComponentMounted();

  const [coaches, setCoaches] = useState([]);
  const [voices, setVoices] = useState(new Map());
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    const [coachesData, voicesCollection] = await Promise.all([
      Coach.getAllActiveCoaches(),
      CoachVoice.getCoachVoices(),
    ]);

    const map = voicesCollection.docs.reduce((prev, voice) => {
      prev.set(voice.id, voice);
      return prev;
    }, new Map());

    setVoices(map);

    setCoaches(
      coachesData.map((coach) => ({
        id: coach.id,
        name: coach.name || `undefined name (${coach.id})`,
        status: map.get(coach.id)?.status,
      })),
    );

    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      if (!isComponentMountedRef.current) return;
      loadData();
    })();
  }, [isComponentMountedRef]);

  const columns = getColumns({
    renderActionCell: useCallback(({ row: { id } }) => {
      const voice = voices.get(id);

      return (
        <PrimaryButton
          variant="info"
          size="medium"
          disabled={voice?.isPending}
          onClick={async () => {
            await remote('coachVoice-requestExtraction', {
              coachId: id,
            });
            await loadData();
          }}
        >
          { voice?.isComplete ? texts.regenerate : texts.generate }
        </PrimaryButton>
      );
    }, [voices, remote]),
  });

  const { filteredRows, toolbarProps } = useQuickSearch(coaches, columns);

  return (
    <Container>
      <HeaderRow>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.subTitle}</SubTitle>
        </TitleContainer>
      </HeaderRow>
      <GenericDataGrid
        rows={filteredRows}
        columns={columns}
        disableSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
        components={{
          Toolbar: QuickSearchToolbar,
        }}
        loading={loading}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            placeholder: texts.toolbarPlaceholder,
          },
        }}
      />
    </Container>
  );
};

export default compose(observer)(CoachVoiceDashboard);
